import React, {useEffect} from 'react'
import styled from '@emotion/styled'
import {OverlayType} from 'modules/overlay/types'

import {Button} from '@vio/atlas/Button'
import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import useShowOverlay from '../../../hooks/useShowOverlay/useShowOverlay'
import {HotelLocationContent} from '../HotelLocationContent'
import {StaticMapImage} from '../HotelLocationMap/StaticMap'
import {
  SelectedElement,
  TrackLocationClick
} from '../HotelLocationMap/TrackLocationClick'
import {LocationAddress} from './LocationAddress'

import type {ContentHotel} from '@findhotel/sapi'

const TextColumnStyle = styled.div`
  display: contents;

  ${linariaMq.desktopSm} {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${cssTheme.layout.spacing.s300};
  }
`

const MapArea = styled.div`
  order: 1;
  padding-block: ${cssTheme.layout.spacing.s200};

  ${linariaMq.desktopSm} {
    order: 0;
    flex: 1;
    padding-block: 0;
    align-self: stretch;
  }
`

const ContentArea = styled.div`
  order: 2;
`

const MoreArea = styled.div`
  order: 3;
  width: fit-content;
`

const StyledTrackLocationClick = styled(TrackLocationClick)`
  width: 100%;
  ${linariaMq.desktopSm} {
    height: 100%;
    display: block;
  }
`

const MapBox = styled.div`
  height: 140px;
  position: relative;
  width: 100%;
  border-radius: ${cssTheme.layout.radius.lg};
  cursor: pointer;
  overflow: hidden;

  ${linariaMq.desktopSm} {
    height: 100%;
    min-height: 240px;
  }
`

interface Props {
  hotel?: ContentHotel
}

export const HotelLocationDetailsCombined: React.FC<Props> = ({hotel}) => {
  const {isMobile, isDesktopXs, isDesktopSm} = useDeviceLayout()
  const {lon, lat} = hotel?._geoloc || {}
  const {location} = hotel?.descriptions || {}
  const {poi: attractions} = hotel?.location || {}
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const dispatchTeamEvent = useDispatchTeamEvent()
  const showOverlay = useShowOverlay()

  const hasAttractions = attractions?.length > 0
  const hasMoreInfo =
    hasAttractions || (location && Object.keys(location).length > 0)

  useEffect(() => {
    if (inView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsLocationSection,
          action: Action.Viewed
        })
      )
    }
  }, [dispatchTeamEvent, inView])

  const handleMapClick = () => {
    if (isMobile || isDesktopXs || isDesktopSm) {
      showOverlay({type: OverlayType.LocationOverlay})
    } else {
      showOverlay({type: OverlayType.LocationInfoOverlay})
    }
  }

  const handleMoreInfoClick = () => {
    showOverlay({type: OverlayType.LocationInfoOverlay})
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="s300"
      desktopSm={{
        flexDirection: 'row',
        columnGap: 's600',
        alignItems: 'flex-start'
      }}
    >
      <TextColumnStyle>
        <div>
          <Text variant="titleL" tag="h2">
            <FormattedMessageWrapper id="location" defaultMessage="Location" />
          </Text>
        </div>
        <div>
          <LocationAddress hotel={hotel} variant="small" />
        </div>
        {hasAttractions && (
          <ContentArea>
            <HotelLocationContent
              variant="apCombined"
              attractions={attractions}
            />
          </ContentArea>
        )}
        {hasMoreInfo && (
          <MoreArea>
            <Box width="fit-content">
              <ButtonLink
                variant="primary"
                size="lg"
                isCompact
                isBold
                onClick={handleMoreInfoClick}
              >
                <FormattedMessageWrapper
                  id="location.moreInfo"
                  defaultMessage="More location info"
                />
              </ButtonLink>
            </Box>
          </MoreArea>
        )}
      </TextColumnStyle>
      <MapArea>
        <StyledTrackLocationClick onClick={handleMapClick}>
          <MapBox ref={ref} data-id={SelectedElement.StaticMap}>
            <StaticMapImage lon={lon} lat={lat} />
            <Box
              display="flex"
              position="absolute"
              right="s300"
              bottom="s300"
              gap="s300"
              data-id={SelectedElement.MapButton}
            >
              <Button
                size="md"
                variant="tertiary"
                isFloating
                iconStart={<Icon name={'Maximize2'} size="sm" />}
              >
                <FormattedMessageWrapper
                  id="openMap"
                  defaultMessage="Open map"
                />
              </Button>
            </Box>
          </MapBox>
        </StyledTrackLocationClick>
      </MapArea>
    </Box>
  )
}
